import React, { memo, useMemo } from 'react';
import DropdownButton from '../../../components/DropdownButtonMC';
import useCustomerChannelsData from '../../../hooks/inbox/data/useCustomerChannelsData';
import useCustomerChannelsActions from '../../../hooks/inbox/actions/useCustomerChannelsActions';
import useConversationItemTimeUI from '../../../hooks/inbox/ui/useConversationItemTimeUI';
import PLATFORM_CONFIG from '../../../utils/platformConfig';

/**
 * Clave corta de canal → entrada de PLATFORM_CONFIG de la que sale el rótulo.
 * Las variantes (WhatsApp BSP / QR / Gupshup, o varias páginas de Messenger) se
 * agrupan bajo un solo canal y se distinguen recién en el segundo nivel.
 */
const CHANNEL_LABEL_SOURCE = {
  whatsapp: 'whatsapp',
  messenger: 'messenger',
  instagram: 'instagram',
  tiktok: 'tik_tok_messages'
};

/**
 * Agrupa los chats por canal conservando el orden por recencia que ya trae la lista.
 *
 * @param {Array<Object>} channels - Canales de useCustomerChannelsData.
 * @returns {Array<{key: string, label: string, icon: string|null, channels: Array<Object>,
 *   isCurrent: boolean, isUnRead: boolean, unreadCount: number}>}
 */
function groupByChannel(channels) {
  const groups = [];
  const indexByKey = {};

  channels.forEach((channel) => {
    const key = channel.sidebarPlatform || channel.platform;
    let group = groups[indexByKey[key]];

    if (!group) {
      const labelSource = PLATFORM_CONFIG[CHANNEL_LABEL_SOURCE[key]] || PLATFORM_CONFIG[channel.platform] || {};
      group = {
        key,
        label: labelSource.name || channel.platformName,
        icon: labelSource.src || channel.platformIcon,
        channels: [],
        isCurrent: false,
        isUnRead: false,
        unreadCount: 0
      };
      indexByKey[key] = groups.length;
      groups.push(group);
    }

    group.channels.push(channel);
    group.isCurrent = group.isCurrent || channel.isCurrent;
    // El no leído del canal agrega el de sus cuentas, así la tira comunica lo mismo
    // que el segundo nivel sin tener que abrirlo.
    group.isUnRead = group.isUnRead || (!channel.isCurrent && channel.isUnRead);
    group.unreadCount += channel.isCurrent ? 0 : channel.unreadCount;
  });

  return groups;
}

/**
 * Contador de no leídos, con el mismo tratamiento que la lista de chats: el círculo
 * aparece siempre que haya no leídos, y el número solo si hay mensajes (un chat
 * marcado no leído a mano tiene el contador en 0).
 */
const UnreadBadge = ({ isUnRead, count }) => {
  if (!isUnRead) return null;

  return (
    <span className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-min-w-4 tw-h-4 tw-rounded-full tw-bg-red-500 tw-px-1">
      <span className="tw-text-2xs tw-font-semibold tw-text-white">
        {count || ''}
      </span>
    </span>
  );
};

const accountLabel = (channel) => channel.accountName || channel.platformName;

/**
 * Fila del segundo nivel: una cuenta del canal.
 */
const AccountRow = ({ channel, howLongAgo }) => (
  <div className="tw-flex tw-w-full tw-min-h-[1.75rem] tw-min-w-0 tw-items-center tw-gap-2">
    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start">
      <span className={`tw-w-full tw-truncate tw-text-left tw-text-default ${channel.isCurrent ? 'tw-text-gray-400' : 'tw-text-gray-700'}`}>
        {accountLabel(channel)}
      </span>
      {channel.accountName && (
        <span className="tw-w-full tw-truncate tw-text-left tw-text-2xs tw-font-normal tw-text-gray-500">
          {channel.platformName}
        </span>
      )}
    </div>
    <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-2xs tw-font-normal tw-text-gray-400">
      {howLongAgo(channel.lastMessageAt)}
    </span>
    {/* Ancho fijo para que los contadores queden en columna, con badge o sin él. */}
    <span className="tw-flex tw-w-5 tw-shrink-0 tw-justify-end">
      <UnreadBadge isUnRead={!channel.isCurrent && channel.isUnRead} count={channel.unreadCount} />
    </span>
  </div>
);

/**
 * DropdownButtonMC invoca `Content` como render function, no lo monta como componente.
 * La fábrica vive fuera del render para no declarar componentes en cada pasada.
 */
const buildAccountRow = (channel, howLongAgo) => () => (
  <AccountRow channel={channel} howLongAgo={howLongAgo} />
);

/**
 * CustomerChannelsSelectorMC - Los canales del customer, en el header del chat.
 *
 * Tira compacta de canales en una sola línea junto al estado: el canal del chat
 * abierto queda resaltado y los demás son links directos. Cuando un canal tiene
 * más de una cuenta (varias páginas, o las variantes de WhatsApp BSP / QR /
 * Gupshup) se despliega un segundo nivel con esas cuentas.
 *
 * No se renderiza cuando el contacto solo tiene el chat actual, que es el caso
 * mayoritario.
 *
 * @param {{ compact?: boolean }} props - `compact` acota el menú de cuentas al
 *   ancho del modal/sidebar para que no se desborde sobre el área de mensajes.
 * @returns {JSX.Element|null}
 */
const CustomerChannelsSelectorMC = memo(({ compact = false }) => {
  const { channels } = useCustomerChannelsData();
  const { handleSelectChannel } = useCustomerChannelsActions();
  const howLongAgo = useConversationItemTimeUI();

  const groups = useMemo(() => groupByChannel(channels), [channels]);
  const hasOtherChats = useMemo(() => channels.some((c) => !c.isCurrent), [channels]);

  const buildAccountItems = (group) => group.channels.map((channel) => ({
    onClick: channel.isCurrent ? undefined : () => handleSelectChannel(channel),
    className: `!tw-py-1.5 [&>span]:!tw-w-full ${channel.isCurrent ? '!tw-bg-gray-50' : ''}`,
    Content: buildAccountRow(channel, howLongAgo)
  }));

  if (!hasOtherChats) return null;

  // Anclado a la izquierda del trigger: el default `right-0` + ancho fijo ancho
  // empujaba el menú hacia la izquierda y tapaba el chat (peor en modal).
  const accountsDropdownClassName = compact
    ? '!tw-left-0 !tw-right-auto !tw-mt-1 !tw-w-[min(16rem,calc(100vw-4rem))] !tw-max-w-[16rem] !tw-z-[60]'
    : '!tw-left-0 !tw-right-auto !tw-mt-1 !tw-w-64 !tw-max-w-[16rem] !tw-z-50';

  return (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-ml-1">
      {groups.map((group, index) => {
        const isSingle = group.channels.length === 1;
        const [only] = group.channels;
        const separator = index > 0 ? 'tw-border-l tw-border-gray-300 tw-pl-1.5 tw-ml-1.5' : '';
        // `!` porque ButtonMC ya trae su propio color de texto en la variante TEXT_PLAIN
        // y entre utilidades Tailwind de igual especificidad no gana el orden del string.
        const tone = group.isCurrent
          ? '!tw-text-green-600 tw-font-semibold'
          : '!tw-text-blue-500 hover:tw-underline';

        return (
          <div key={group.key} className={`tw-relative tw-flex tw-shrink-0 tw-items-center ${separator}`}>
            {isSingle ? (
              <button
                type="button"
                onClick={only.isCurrent ? undefined : () => handleSelectChannel(only)}
                title={[group.label, only.accountName].filter(Boolean).join(' · ')}
                className={`tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-p-0 tw-text-2xs ${tone} ${only.isCurrent ? 'tw-cursor-default' : ''}`}
              >
                <span>{group.label}</span>
                <UnreadBadge isUnRead={group.isUnRead} count={group.unreadCount} />
              </button>
            ) : (
              <DropdownButton
                variant="TEXT_PLAIN"
                items={buildAccountItems(group)}
                className={`!tw-ml-0 !tw-h-[20px] !tw-min-h-0 !tw-py-0 !tw-px-0 !tw-text-2xs ${tone}`}
                dropdownClassName={accountsDropdownClassName}
                renderContent={() => (
                  <span className={`tw-flex tw-items-center tw-gap-1 ${tone}`}>
                    <span>{group.label}</span>
                    <span className="tw-shrink-0 tw-text-2xs tw-font-normal tw-text-gray-400">
                      {group.channels.length}
                    </span>
                    <UnreadBadge isUnRead={group.isUnRead} count={group.unreadCount} />
                  </span>
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

CustomerChannelsSelectorMC.displayName = 'CustomerChannelsSelectorMC';

export default CustomerChannelsSelectorMC;
